import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '@/lib/supabase'

type Theme = 'light' | 'dark' | 'yellow-black'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => Promise<void>
  loading: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [theme, setThemeState] = useState<Theme>('light')
  const [loading, setLoading] = useState(true)

  // Load theme and text size from user profile or localStorage
  useEffect(() => {
    const loadTheme = async () => {
      if (user) {
        // Load from user profile
        const { data, error } = await supabase
          .from('profiles')
          .select('a11y_prefs')
          .eq('id', user.id)
          .maybeSingle()

        if (!error && data?.a11y_prefs) {
          // Load theme
          if (data.a11y_prefs.theme) {
            const userTheme = data.a11y_prefs.theme as Theme
            if (['light', 'dark', 'yellow-black'].includes(userTheme)) {
              setThemeState(userTheme)
              applyTheme(userTheme)
            } else {
              setThemeState('light')
              applyTheme('light')
            }
          } else {
            const savedTheme = localStorage.getItem('theme') as Theme | null
            if (savedTheme && ['light', 'dark', 'yellow-black'].includes(savedTheme)) {
              setThemeState(savedTheme)
              applyTheme(savedTheme)
            } else {
              setThemeState('light')
              applyTheme('light')
            }
          }

          // Apply text size
          if (data.a11y_prefs.textSize) {
            const textSizeMap: Record<string, number> = {
              small: 0.875,
              medium: 1.125,
              large: 1.5,
              'x-large': 2.0,
            }
            const sizeMultiplier = textSizeMap[data.a11y_prefs.textSize] || 1.125
            document.documentElement.style.fontSize = `${18 * sizeMultiplier}px`
          }
        } else {
          // Fallback to localStorage or default
          const savedTheme = localStorage.getItem('theme') as Theme | null
          if (savedTheme && ['light', 'dark', 'yellow-black'].includes(savedTheme)) {
            setThemeState(savedTheme)
            applyTheme(savedTheme)
          } else {
            setThemeState('light')
            applyTheme('light')
          }
        }
      } else {
        // Not logged in - use localStorage or default
        const savedTheme = localStorage.getItem('theme') as Theme | null
        if (savedTheme && ['light', 'dark', 'yellow-black'].includes(savedTheme)) {
          setThemeState(savedTheme)
          applyTheme(savedTheme)
        } else {
          setThemeState('light')
          applyTheme('light')
        }
      }
      setLoading(false)
    }

    loadTheme()
  }, [user])

  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement
    if (newTheme === 'light') {
      root.removeAttribute('data-theme')
    } else {
      root.setAttribute('data-theme', newTheme)
    }
  }

  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme)
    applyTheme(newTheme)
    localStorage.setItem('theme', newTheme)

    // Save to user profile if logged in
    if (user) {
      // Get existing a11y_prefs to merge
      const { data: profile } = await supabase
        .from('profiles')
        .select('a11y_prefs')
        .eq('id', user.id)
        .maybeSingle()

      const existingPrefs = profile?.a11y_prefs || {}

      const { error } = await supabase
        .from('profiles')
        .update({
          a11y_prefs: {
            ...existingPrefs,
            theme: newTheme,
          },
        })
        .eq('id', user.id)

      if (error) {
        console.error('Failed to save theme preference:', error)
      }
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

